import { VaccinePills } from '@/components/VaccinePills';
import { ApiError, kioskComplete, listServices } from '@/lib/api';
import type { KioskCurrentResponse, ServiceCatalogueItem } from '@/lib/types';
import { Check, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { CheckinQuestion } from './CheckinQuestion';

// -------------------------------------------------------------------
// Sub-spec 06 Plan 3 — intake questions are now tenant-editable.
// The server returns `label` on each question; this fallback map keeps
// helpful descriptions for the five default slugs. Tenant-overridden
// labels render the server-provided label verbatim with no description.
// -------------------------------------------------------------------
const INTAKE_QUESTION_FALLBACKS: Record<string, { question: string; description: string }> = {
  fed_together: {
    question: 'Can your pets be fed together?',
    description: 'Since they are sharing a kennel/pen',
  },
  can_socialise: {
    question: 'Can your dog(s) socialise with other dogs?',
    description: 'During supervised play time',
  },
  photo_consent: {
    question: 'Can we photograph your pet(s) for social media?',
    description: 'We love sharing happy guests',
  },
  medical_proxy: {
    question: 'Do you confirm we can act as medical proxy in an emergency?',
    description: 'We will always try to contact you first.',
  },
  belongings_disclaimer: {
    question: 'I understand belongings may not be returned',
    description: 'With many similar items in the wash, we cannot guarantee.',
  },
};

type QuestionAnswers = Record<
  string,
  { answer: boolean | 0 | 1; note?: string | null | undefined }
>;

interface PetState {
  feeding: string;
  medication: string;
  selectedServiceIds: Set<string>;
  questions: QuestionAnswers;
  submitting: boolean;
  error: string | null;
  done: boolean;
}

interface Props {
  current: KioskCurrentResponse;
  onComplete: () => void;
}

export function CheckinForm({ current, onComplete }: Props) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [petStates, setPetStates] = useState<PetState[]>(() =>
    current.pets.map((pet) => {
      // Pre-fill answers from pastResponses if the intake was partially done before.
      const questions: QuestionAnswers = {};
      for (const r of pet.pastResponses) {
        questions[r.questionSlug] = {
          answer: r.answer as 0 | 1,
          note: r.note ?? undefined,
        };
      }
      return {
        feeding: pet.feeding ?? '',
        medication: pet.medication ?? '',
        selectedServiceIds: new Set<string>(),
        questions,
        submitting: false,
        error: null,
        done: false,
      };
    }),
  );
  const [services, setServices] = useState<ServiceCatalogueItem[]>([]);

  useEffect(() => {
    listServices()
      .then((all) => {
        setServices(
          all.filter((s) => {
            try {
              const a = JSON.parse(s.applicableTo) as Record<string, boolean>;
              return a.boarding_addon === true || a.daycare_addon === true;
            } catch {
              return false;
            }
          }),
        );
      })
      .catch(() => {
        // Non-fatal: addon services just won't show.
      });
  }, []);

  const updateState = (index: number, patch: Partial<PetState>) => {
    setPetStates((prev) => {
      const existing = prev[index];
      if (!existing) return prev;
      const next = [...prev];
      next[index] = { ...existing, ...patch };
      return next;
    });
  };

  const handleServiceToggle = (index: number, serviceId: string) => {
    const existing = petStates[index];
    if (!existing) return;
    const next = new Set(existing.selectedServiceIds);
    if (next.has(serviceId)) {
      next.delete(serviceId);
    } else {
      next.add(serviceId);
    }
    updateState(index, { selectedServiceIds: next });
  };

  const handleQuestionChange = (
    index: number,
    slug: string,
    value: { answer: boolean | 0 | 1; note?: string | null | undefined },
  ) => {
    const existing = petStates[index];
    if (!existing) return;
    updateState(index, {
      questions: { ...existing.questions, [slug]: value },
    });
  };

  const handleSubmitPet = async (index: number) => {
    const pet = current.pets[index];
    const state = petStates[index];
    if (!pet || !state) return;

    // Validate required questions
    for (const q of current.questions) {
      if (q.required && !(q.slug in state.questions)) {
        updateState(index, { error: 'Please answer all required questions before continuing.' });
        return;
      }
    }

    updateState(index, { submitting: true, error: null });

    try {
      await kioskComplete(pet.bookingId, {
        feeding: state.feeding || null,
        medication: state.medication || null,
        selectedServiceIds: Array.from(state.selectedServiceIds),
        questions: state.questions,
      });
      // Compute the updated states locally so we can make decisions
      // about the next step before committing to React state.
      const updatedStates = petStates.map((s, j) =>
        j === index ? { ...s, done: true, submitting: false } : s,
      );
      setPetStates(updatedStates);

      const nextIncomplete = updatedStates.findIndex((s, j) => j > index && !s.done);
      if (nextIncomplete !== -1) {
        setActiveIndex(nextIncomplete);
      } else if (updatedStates.every((s) => s.done)) {
        onComplete();
      }
    } catch (e: unknown) {
      const msg =
        e instanceof ApiError
          ? `Error ${e.status}: ${e.code}`
          : 'Something went wrong. Please try again.';
      updateState(index, { error: msg, submitting: false });
    }
  };

  const allDone = petStates.every((s) => s.done);

  if (allDone) {
    return (
      <div className="grid min-h-[100dvh] place-items-center bg-background px-6 text-center">
        <div>
          <div className="mx-auto mb-6 grid size-14 place-items-center rounded-full bg-success/15 text-success">
            <Check className="size-7" strokeWidth={2} />
          </div>
          <h2 className="font-heading text-2xl font-medium tracking-tight">Check-in complete</h2>
          <p className="mt-2 text-base text-muted-foreground">
            Thanks! The front desk has been notified.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-background">
      {/* Pet tabs */}
      {current.pets.length > 1 && (
        <div className="border-b border-border bg-surface">
          <div className="mx-auto flex max-w-2xl gap-1 overflow-x-auto px-4 py-3">
            {current.pets.map((pet, i) => {
              const done = petStates[i]?.done ?? false;
              return (
                <button
                  key={pet.bookingId}
                  type="button"
                  onClick={() => !done && setActiveIndex(i)}
                  className={[
                    'flex items-center gap-2 whitespace-nowrap rounded-lg px-4 py-2.5 text-sm font-medium transition-colors press',
                    activeIndex === i && !done
                      ? 'bg-primary text-primary-foreground'
                      : done
                        ? 'bg-success/10 text-success'
                        : 'bg-transparent text-foreground border border-border',
                  ].join(' ')}
                >
                  {done && <Check className="size-3.5" strokeWidth={2.5} />}
                  {pet.petName}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Active pet form */}
      <div className="mx-auto max-w-2xl px-4 py-8 space-y-6">
        {current.pets.map((pet, i) => {
          if (i !== activeIndex) return null;
          const state = petStates[i];
          if (!state) return null;

          return (
            <div key={pet.bookingId} className="space-y-6">
              {/* Pet header */}
              <div>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
                    {pet.customerName}
                  </p>
                  {pet.customerId && (
                    <a
                      href={`/customers/${pet.customerId}/edit`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10.5px] font-medium text-primary hover:underline"
                    >
                      Edit customer ↗
                    </a>
                  )}
                </div>
                <h1 className="font-heading text-3xl font-medium tracking-tight">{pet.petName}</h1>
                <p className="mt-0.5 text-base text-muted-foreground capitalize">
                  {pet.species}
                  {pet.kennel && (
                    <>
                      {' · '}
                      <span className="num">{pet.kennel}</span>
                    </>
                  )}
                </p>
              </div>

              {/* Vaccinations */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Vaccines on file
                  </p>
                  {pet.petId && pet.customerId && (
                    <a
                      href={`/customers/${pet.customerId}/pets/${pet.petId}/edit`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`text-[10.5px] font-medium hover:underline ${
                        pet.vaccinations.length === 0 ? 'text-amber-600' : 'text-primary'
                      }`}
                    >
                      {pet.vaccinations.length === 0 ? 'No vaccines — add ↗' : 'Update vaccines ↗'}
                    </a>
                  )}
                </div>
                {pet.vaccinations.length > 0 && <VaccinePills vaccinations={pet.vaccinations} />}
              </div>

              {/* Feeding + medication */}
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor={`feeding-${i}`}
                    className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                  >
                    Feeding instructions
                  </label>
                  <textarea
                    id={`feeding-${i}`}
                    rows={3}
                    value={state.feeding}
                    onChange={(e) => updateState(i, { feeding: e.target.value })}
                    placeholder="e.g. 200g dry food twice a day…"
                    className="w-full resize-none rounded-lg border border-border bg-surface px-3 py-3 text-base outline-none focus:ring-2 focus:ring-ring/30"
                  />
                </div>
                <div>
                  <label
                    htmlFor={`medication-${i}`}
                    className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                  >
                    Medication
                  </label>
                  <textarea
                    id={`medication-${i}`}
                    rows={2}
                    value={state.medication}
                    onChange={(e) => updateState(i, { medication: e.target.value })}
                    placeholder="None, or describe dosage and timing…"
                    className="w-full resize-none rounded-lg border border-border bg-surface px-3 py-3 text-base outline-none focus:ring-2 focus:ring-ring/30"
                  />
                </div>
              </div>

              {/* Service add-ons */}
              {services.length > 0 && (
                <div className="space-y-3">
                  <p className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Add-on services
                  </p>
                  <div className="space-y-2">
                    {services.map((svc) => {
                      const checked = state.selectedServiceIds.has(svc.id);
                      return (
                        <label
                          key={svc.id}
                          className="flex cursor-pointer items-center gap-4 rounded-xl border border-border bg-surface p-4 press"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => handleServiceToggle(i, svc.id)}
                            className="size-5 cursor-pointer accent-primary"
                          />
                          <div className="flex-1">
                            <p className="text-base font-medium">{svc.name}</p>
                            {svc.description && (
                              <p className="text-sm text-muted-foreground">{svc.description}</p>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Intake questions */}
              {current.questions.length > 0 && (
                <div className="space-y-3">
                  <p className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    A few quick questions
                  </p>
                  {current.questions.map((q) => {
                    // Server-provided label wins (tenant-editable). Fall back
                    // to the Phase 2 hardcoded copy for the five default
                    // slugs, then to the slug itself if neither matches.
                    const fallback = INTAKE_QUESTION_FALLBACKS[q.slug];
                    const question = q.label ?? fallback?.question ?? q.slug;
                    const description = q.label ? '' : (fallback?.description ?? '');
                    return (
                      <CheckinQuestion
                        key={q.slug}
                        slug={q.slug}
                        question={question}
                        description={description}
                        required={q.required}
                        isAcknowledgement={q.isAcknowledgement}
                        value={state.questions[q.slug]}
                        onChange={(slug, value) => handleQuestionChange(i, slug, value)}
                      />
                    );
                  })}
                </div>
              )}

              {state.error && (
                <p className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
                  {state.error}
                </p>
              )}

              <button
                type="button"
                onClick={() => void handleSubmitPet(i)}
                disabled={state.submitting}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 py-4 text-base font-medium text-primary-foreground press disabled:opacity-50"
              >
                {state.submitting && <Loader2 className="size-5 animate-spin" strokeWidth={1.75} />}
                {i < current.pets.length - 1 ? `Done with ${pet.petName}` : 'Complete check-in'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
